import type { AllTranslationKeys } from '@@fire-base/i18n'
import type { FSnackbarTypes } from '@fire-framework/components/snackbar/FSnackbarTypes'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface SnackbarOptions {
  icon?: string
  timeout?: number
  type?: FSnackbarTypes
  multiLine?: boolean
  action?: {
    to?: string
    color?: string
    label?: AllTranslationKeys
  }
  [key: string]: unknown
}

export const useSnackbarStore = defineStore('snackbar', () => {
  const open = ref(false)
  const message = ref('')
  const icon = ref<string | undefined>(undefined)
  const timeout = ref(5000)
  const multiLine = ref<boolean | undefined>(false)
  const options = ref<SnackbarOptions>({})
  const timeoutId = ref<ReturnType<typeof setTimeout> | null>(null)
  const type = ref<FSnackbarTypes>('default')

  function show(newMessage: string, opts: SnackbarOptions = {}) {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
      timeoutId.value = null
    }

    message.value = newMessage
    icon.value = opts.icon
    multiLine.value = opts.multiLine ?? true
    timeout.value = opts.timeout ?? 5000
    type.value = opts.type ?? 'default'

    const { mode, icon: _i, timeout: _t, ...rest } = opts
    options.value = rest

    open.value = true

    if (timeout.value > 0) {
      timeoutId.value = setTimeout(() => {
        hide()
      }, timeout.value)
    }
  }

  function success(newMessage: string, opts: SnackbarOptions = {}) {
    show(newMessage, {
      icon: 'icon-circle-check',
      type: 'success',
      ...opts,
    })
  }

  function error(newMessage: string, opts: SnackbarOptions = {}) {
    show(newMessage, {
      icon: 'icon-circle-exclamation',
      type: 'critical',
      ...opts,
    })
  }

  function warning(newMessage: string, opts: SnackbarOptions = {}) {
    show(newMessage, {
      icon: 'icon-circle-question',
      type: 'warning',
      ...opts,
    })
  }

  function info(newMessage: string, opts: SnackbarOptions = {}) {
    show(newMessage, {
      icon: 'icon-circle-information',
      type: 'information',
      ...opts,
    })
  }

  function openOrderSuccess(newMessage: string, opts: SnackbarOptions = {}) {
    const defaultAction = {
      to: '/orders',
      color: 'tertiary',
      label: 'navigation.continueToCart' as AllTranslationKeys,
    }

    show(newMessage, {
      icon: 'icon-circle-check',
      type: 'success',
      ...opts,
      action: {
        ...defaultAction,
        ...opts.action,
      },
    })
  }

  function hide() {
    if (timeoutId.value) {
      clearTimeout(timeoutId.value)
      timeoutId.value = null
    }
    open.value = false
  }

  return {
    open,
    message,
    icon,
    multiLine,
    type,
    timeout,
    options,
    timeoutId,
    show,
    success,
    error,
    warning,
    info,
    hide,
    openOrderSuccess,
  }
})
